import {Button} from '@orbit-ui/components/button';
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '@orbit-ui/components/card';
import {Container} from '@orbit-ui/components/container';
import {PageHeader} from '@orbit-ui/components/page-header';
import {LogoCosmonicIcon} from '@orbit-ui/icons';
import {createFileRoute} from '@tanstack/react-router';
import {LoaderCircleIcon} from 'lucide-react';
import * as React from 'react';
import {PageContent} from '@/components/page-content';

import {PageWrapper} from '@/components/page-wrapper';
import {usePermissions} from '@/features/auth/hooks/use-permissions';
import {useGenerateAdminPortalLinkMutation} from '@/features/core/api/api-hooks';

/**
 * Response type for the admin portal link API
 * Matches the AdminPortalLinkResponse struct in backend/internal/handlers/organizations.go
 */
type AdminPortalLinkResponse = {
  url: string;
};

// Define the route
export const Route = createFileRoute('/_app/admin/sso')({
  component: SSOConfigPage,
  async beforeLoad() {
    return {
      title: 'Organization Settings',
      breadcrumb: {
        label: 'Organization Settings',
        path: '/admin/sso',
      },
    };
  },
});

function SSOConfigPage() {
  const {isSsoAdmin, getOrganizationId, isLoading: permissionsLoading} = usePermissions();
  const [error, setError] = React.useState<Error | null>(null);

  // Get organization ID directly from user context
  const organizationId = getOrganizationId();

  // Use the React Query mutation hook
  const generatePortalLinkMutation = useGenerateAdminPortalLinkMutation();

  // Handle button click to generate and open the SSO admin portal link
  const handleGenerateAndOpenLink = async () => {
    try {
      setError(null);

      // Generate the admin portal link
      const response = (await generatePortalLinkMutation.mutateAsync({
        organization_id: organizationId,
      })) as AdminPortalLinkResponse;

      // Open the link in a new tab
      window.open(response.url, '_blank', 'noopener,noreferrer');
    } catch (error_) {
      setError(error_ instanceof Error ? error_ : new Error('Unknown error'));
      console.error('Error generating portal link:', error_);
    }
  };

  // Show loading state while permissions are being loaded
  if (permissionsLoading) {
    return (
      <PageWrapper>
        <PageContent className="p-6">
          <div className="flex h-64 items-center justify-center">
            <LoaderCircleIcon className="mr-2 size-8 animate-spin" />
            <span>Loading...</span>
          </div>
        </PageContent>
      </PageWrapper>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <PageWrapper>
        <PageContent className="p-6">
          <div className="text-red-500">
            <h1 className="mb-2 text-2xl font-bold">Error</h1>
            <p>{error.message}</p>
          </div>
        </PageContent>
      </PageWrapper>
    );
  }

  // Check if user is an admin
  if (!isSsoAdmin()) {
    return (
      <PageWrapper>
        <PageContent className="p-6">
          <div className="text-red-500">
            <h1 className="mb-2 text-2xl font-bold">Access Denied</h1>
            <p>You must be an organization administrator to access this page.</p>
          </div>
        </PageContent>
      </PageWrapper>
    );
  }

  // Check if organization ID is available
  if (!organizationId) {
    return (
      <PageWrapper>
        <PageContent className="p-6">
          <div className="text-red-500">
            <h1 className="mb-2 text-2xl font-bold">Error</h1>
            <p>Could not determine your organization ID.</p>
          </div>
        </PageContent>
      </PageWrapper>
    );
  }

  return (
    <Container>
      <PageHeader
        title={
          <div className="flex items-center gap-2">
            <LogoCosmonicIcon />
            <div>Organization Settings</div>
          </div>
        }
      >
        Configure organization-wide settings, such as who can sign in, who can create users, and
        configure SSO. Stuff and things.
      </PageHeader>
      <h2 className="mb-8 text-2xl font-bold">SSO configuration</h2>

      <Card className="w-full">
        <CardHeader>
          <CardTitle>SSO Configuration</CardTitle>

          <CardDescription>Configure Single Sign-On for your organization</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-600">
              Click the button below to open the SSO configuration portal in a new tab.
            </p>
            <Button
              onClick={handleGenerateAndOpenLink}
              disabled={generatePortalLinkMutation.isPending}
              variant="outline"
            >
              {generatePortalLinkMutation.isPending ? (
                <>
                  <LoaderCircleIcon className="mr-2 size-4 animate-spin" />
                  Loading...
                </>
              ) : (
                'Open SSO Admin Portal'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </Container>
  );
}
